import {
  Search,
  X,
  Settings2,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import TablePagination from "@/components/ui/table-pagination.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  getStatusColorClasses,
  getStatusBadgeClass,
  getStatusDotClass,
} from "@/lib/status-utils";
import {
  getStatusDisplayText,
  stripStatusTail,
  formatStatusDisplay,
} from "@/lib/formatting-utils";
import { formatTimestamp } from "@/lib/datetime-utils";
import { cardStyles, outlineChromeButton, outlineIconButton, searchStyles } from "@/lib/status-styles";
import { cn, mathUtils } from "@/lib/utils";
import LoadingIndicator from "@/components/ui/loading-indicator";

const ASSET_PROPERTY_NAMES = new Set([
  "assetNumber",
  "lease",
  "status",
  "lastUpdated",
]);

const RUN_STATUS_TAG_NAME = "RUN STATUS";

/**
 * Operating Points Table Component
 * @param {Object} props
 * @param {Object} props.data - Data props
 * @param {Array} props.data.measurementColumns - Array of measurement column definitions
 * @param {Array} props.data.sortedAssets - Array of sorted assets
 * @param {Array} props.data.paginatedAssets - Array of assets for current page
 * @param {boolean} props.data.isLoading - Whether asset data is loading
 * @param {Object} props.sort - Sort props
 * @param {string|null} props.sort.column - Currently sorted column
 * @param {string} props.sort.direction - Sort direction ('asc' or 'desc')
 * @param {Function} props.sort.onSort - Callback when column is clicked for sorting
 * @param {Object} props.columnActions - Column action callbacks
 * @param {Function} props.columnActions.onEdit - Callback when column edit button is clicked
 * @param {Function} props.columnActions.onMoveLeft - Callback when column move left button is clicked
 * @param {Function} props.columnActions.onMoveRight - Callback when column move right button is clicked
 * @param {Function} props.columnActions.onAdd - Callback when add column button is clicked
 * @param {Object} props.tagData - Tag data props
 * @param {Function} props.tagData.getValueForAsset - Function to get tag value for asset/column
 * @param {Set<string>} props.tagData.fetchingAssetIds - Set of asset IDs currently being fetched
 * @param {Function} props.tagData.getTagData - Function to get tag data for an asset ID
 * @param {Object} props.search - Search props
 * @param {string} props.search.value - Search query string
 * @param {Function} props.search.onChange - Callback when search changes
 * @param {Object} props.save - Save props
 * @param {Function} props.save.onSave - Callback when save button is clicked
 * @param {boolean} props.save.isSaving - Whether save is in progress
 * @param {boolean} props.save.hasUnsavedChanges - Whether there are unsaved changes
 * @param {Function} props.onAssetClick - Callback when asset row is clicked
 * @param {Object} props.pagination - Pagination props
 * @param {number} props.pagination.currentPage - Current page number
 * @param {number} props.pagination.totalPages - Total number of pages
 * @param {number} props.pagination.startIndex - Start index for pagination
 * @param {number} props.pagination.itemsPerPage - Number of rows per page
 * @param {number} props.pagination.totalItems - Total number of items
 * @param {Function} props.pagination.onPageChange - Callback when page changes
 * @param {Function} props.pagination.onItemsPerPageChange - Callback when rows per page changes
 */
export default function OperatingPointsTable({
  data,
  sort,
  columnActions,
  tagData,
  search,
  save,
  onAssetClick,
  pagination,
}) {
  // Destructure grouped props for easier access
  const { measurementColumns, sortedAssets, paginatedAssets, isLoading } = data;
  const { column: sortColumn, direction: sortDirection, onSort } = sort;
  const {
    onEdit: onEditColumn,
    onMoveLeft: onMoveColumnLeft,
    onMoveRight: onMoveColumnRight,
    onAdd: onAddColumn,
  } = columnActions;
  const {
    getValueForAsset: getTagValueForAsset,
    fetchingAssetIds,
    getTagData,
  } = tagData;
  const { value: searchValue, onChange: onSearchChange } = search;
  const { onSave, isSaving, hasUnsavedChanges } = save;
  const {
    currentPage,
    totalPages,
    startIndex,
    itemsPerPage,
    totalItems,
    onPageChange,
    onItemsPerPageChange,
  } = pagination;
  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortDirection === "asc" ? (
        <ChevronUp className='h-4 w-4 ml-1 text-muted-foreground' />
      ) : (
        <ChevronDown className='h-4 w-4 ml-1 text-muted-foreground' />
      );
    }
    return (
      <ChevronsUpDown className='h-4 w-4 ml-1 text-muted-foreground opacity-50' />
    );
  };

  const isRunStatusColumn = (c) => {
    const name = (c.tagName || c.key || c.labelName || "").toString().trim();
    return name.toUpperCase() === RUN_STATUS_TAG_NAME;
  };

  const findRunStatusParam = (tagData) => {
    if (!tagData) return null;
    return (
      Object.values(tagData)
        .flat()
        .find(
          (p) =>
            p?.parameter
            && String(p.parameter).toUpperCase().trim() === RUN_STATUS_TAG_NAME
        ) ?? null
    );
  };

  /** Map API status to getStatusColorClasses key (PascalCase; NoData normalized). */
  const getRunStatusColorKey = (apiStatus) => {
    if (!apiStatus) return "Unknown";
    if (apiStatus === "No Data" || apiStatus === "NoData") return "NoData";
    return apiStatus.charAt(0).toUpperCase() + apiStatus.slice(1).toLowerCase();
  };

  const renderMeasurementCell = (asset, col) => {
    const value = getTagValueForAsset(asset, col);
    const isAssetProperty =
      col.tagName && ASSET_PROPERTY_NAMES.has(col.tagName);

    // Check if tag data is loading for this asset
    let isLoadingTagData = false;
    if (
      !isAssetProperty
      && (col.tagName || col.universalDescriptorId != null)
      && asset?.id
      && typeof asset.id === "string"
    ) {
      const assetId = asset.id.trim();
      const tagDataEntry = getTagData(assetId);
      isLoadingTagData =
        tagDataEntry?.isLoading || fetchingAssetIds.has(assetId);
    }

    // Format the value based on type
    let displayValue = "--";
    if (value !== null && value !== undefined) {
      if (isAssetProperty) {
        // For asset properties, format appropriately
        if (col.tagName === "status") {
          displayValue = getStatusDisplayText(value);
        } else if (col.tagName === "lastUpdated") {
          displayValue = formatTimestamp(value);
        } else {
          displayValue = String(value);
        }
      } else {
        // For tag-based measurements, show with unit if available
        displayValue = col.unit ? `${value} ${col.unit}` : String(value);
      }
    }

    const isRunStatus = isRunStatusColumn(col) && value != null && value !== "";
    let runStatusLabel = null;
    let runStatusColorKey = null;
    if (isRunStatus) {
      runStatusLabel = formatStatusDisplay(stripStatusTail(String(value)));
      const runStatusParam = asset?.id
        ? findRunStatusParam(getTagData(asset.id)?.tagData)
        : null;
      if (runStatusParam) {
        runStatusColorKey = getRunStatusColorKey(runStatusParam.status);
      } else {
        const first = stripStatusTail(String(value)).split(/\s+/)[0] || "";
        const firstLower = first.toLowerCase();
        const isLastOrNumeric =
          firstLower === "last"
          || firstLower.startsWith("last:")
          || (first !== "" && Number.isFinite(Number.parseFloat(first)));
        runStatusColorKey = isLastOrNumeric
          ? "Normal"
          : getStatusDisplayText(first);
      }
    }

    return (
      <TableCell
        key={col.key}
        className='text-sm text-foreground font-normal py-3 px-4 w-28 min-w-28'>
        {isAssetProperty && col.tagName === "status" ? (
          <Badge variant='status' className={getStatusBadgeClass(value)}>
            <div
              className={getStatusDotClass(
                value,
                "w-1.5 h-1.5 rounded-full"
              )}
            />
            {displayValue}
          </Badge>
        ) : runStatusLabel !== null ? (
          <div
            className={cn(
              "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold",
              getStatusColorClasses(runStatusColorKey || "Unknown")
            )}
            title={`Status: ${runStatusLabel}`}>
            {runStatusLabel}
          </div>
        ) : isLoadingTagData ? (
          <div className='flex items-center justify-center'>
            <LoadingIndicator size='sm' showText />
          </div>
        ) : (
          <div className='truncate'>{displayValue}</div>
        )}
      </TableCell>
    );
  };

  const adjustedCurrentPage = currentPage > totalPages ? 1 : currentPage;

  return (
    <>
      {/* Table Header with Search and Add Column */}
      <CardHeader className='shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card px-6 py-4 border-b border-border'>
        <CardTitle className='text-lg font-semibold text-foreground'>
          Operating points
        </CardTitle>
        <div className='flex items-center gap-4'>
          {/* Save Button - Always visible, disabled when no changes */}
          <Button
            variant='outline'
            size='sm'
            onClick={onSave}
            disabled={isSaving || !hasUnsavedChanges}
            className='font-normal text-sm border border-green-600 bg-green-600 text-white hover:bg-green-700 hover:border-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-muted disabled:border-border disabled:text-muted-foreground'>
            {isSaving ? "Saving..." : "Save"}
          </Button>
          {/* Add Column Button */}
          <Button
            variant='outline'
            size='sm'
            onClick={onAddColumn}
            className={outlineChromeButton}>
            Add Column
          </Button>
          {/* Search */}
          <div className='relative'>
            <Input
              type='text'
              placeholder='Search assets...'
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className='pr-10 w-full'
            />
            {searchValue ? (
              <button
                onClick={() => onSearchChange("")}
                className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground cursor-pointer hover:text-foreground transition-colors z-10'
                title='Clear search'>
                <X className='h-4 w-4' />
              </button>
            ) : (
              <Search className={searchStyles.icon} />
            )}
          </div>
        </div>
      </CardHeader>

      {/* Table — fills remaining viewport height */}
      <CardContent
        className={cn(cardStyles.content, "flex flex-col flex-1 min-h-0")}>
        <div
          className={cn(
            cardStyles.tableContainer,
            cardStyles.tableFillBody
          )}>
          <Table className='min-w-full'>
            <TableHeader className={cardStyles.tableHeader}>
              <TableRow className={cardStyles.tableRow}>
                <TableHead
                  className={cn(cardStyles.tableHead, "w-24 min-w-24")}
                  onClick={() => onSort("assetNumber")}
                  data-testid='header-asset'>
                  <div className='flex items-center'>
                    Asset Number
                    {getSortIcon("assetNumber")}
                  </div>
                </TableHead>
                <TableHead
                  className={cn(cardStyles.tableHead, "w-24 min-w-24")}
                  onClick={() => onSort("lease")}
                  data-testid='header-lease'>
                  <div className='flex items-center'>
                    Lease Name
                    {getSortIcon("lease")}
                  </div>
                </TableHead>
                <TableHead
                  className={cn(cardStyles.tableHead, "w-20 min-w-20")}
                  onClick={() => onSort("status")}
                  data-testid='header-status'>
                  <div className='flex items-center'>
                    Status
                    {getSortIcon("status")}
                  </div>
                </TableHead>
                <TableHead
                  className={cn(cardStyles.tableHead, "w-24 min-w-24")}
                  onClick={() => onSort("lastUpdated")}
                  data-testid='header-last-rpt'>
                  <div className='flex items-center'>
                    Last Report
                    {getSortIcon("lastUpdated")}
                  </div>
                </TableHead>
                {measurementColumns.map((col, colIndex) => (
                  <TableHead
                    key={col.key}
                    className={cn(cardStyles.tableHead, "w-28 min-w-28")}
                    onClick={() => onSort(col.key)}
                    data-testid={`header-${col.key}`}>
                    <div className='flex flex-col gap-1'>
                      <div className='flex items-center'>
                        {col.labelName ?? ""}
                        {getSortIcon(col.key)}
                      </div>
                      <div
                        className='flex items-center justify-start gap-0.5'
                        onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onMoveColumnLeft?.(col, colIndex);
                          }}
                          disabled={colIndex === 0}
                          className={outlineIconButton}
                          title='Move column left'>
                          <ArrowLeft className='h-3 w-3' />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditColumn(col);
                          }}
                          className={outlineIconButton}
                          title='Edit column'>
                          <Settings2 className='h-3.5 w-3.5' />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onMoveColumnRight?.(col, colIndex);
                          }}
                          disabled={colIndex === measurementColumns.length - 1}
                          className={outlineIconButton}
                          title='Move column right'>
                          <ArrowRight className='h-3 w-3' />
                        </button>
                      </div>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={4 + measurementColumns.length}
                    className='text-center py-8 text-muted-foreground'>
                    Loading asset data...
                  </TableCell>
                </TableRow>
              ) : sortedAssets.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4 + measurementColumns.length}
                    className='text-center py-8 text-muted-foreground'>
                    No assets found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedAssets.map((asset) => {
                  const isOptimizationEnabled = asset.optimizationEnabled;
                  return (
                    <TableRow
                      key={asset.id || asset.assetNumber}
                      className={cn(cardStyles.bodyRow, "cursor-pointer")}
                      onClick={() => onAssetClick(asset)}>
                      <TableCell
                        className={cn(
                          cardStyles.tableCell,
                          "w-24 min-w-24 relative",
                          isOptimizationEnabled && "pl-5"
                        )}>
                        {isOptimizationEnabled && (
                          <div
                            className='absolute left-0 top-0 bottom-0 w-[3px] bg-brand-cyan z-0'
                            aria-hidden
                          />
                        )}
                        <span className='relative z-0'>
                          <div className='truncate'>{asset.assetNumber}</div>
                        </span>
                      </TableCell>
                      <TableCell
                        className={cn(cardStyles.tableCell, "w-24 min-w-24")}>
                        <div className='text-muted-foreground truncate'>
                          {asset.lease || "-"}
                        </div>
                      </TableCell>
                      <TableCell className='w-20 min-w-20'>
                        <Badge
                          variant='status'
                          className={getStatusBadgeClass(asset.status)}>
                          <div
                            className={getStatusDotClass(
                              asset.status,
                              "w-1.5 h-1.5 rounded-full"
                            )}
                          />
                          {getStatusDisplayText(asset.status)}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className={cn(cardStyles.tableCell, "w-24 min-w-24")}>
                        <div className='truncate'>
                          {asset.lastUpdated
                            ? formatTimestamp(asset.lastUpdated)
                            : "-"}
                        </div>
                      </TableCell>
                      {measurementColumns.map((col) =>
                        renderMeasurementCell(asset, col)
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className='shrink-0'>
          <TablePagination
            currentPage={adjustedCurrentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            startIndex={startIndex}
            onPageChange={onPageChange}
            onItemsPerPageChange={onItemsPerPageChange}
            itemsLabel='results'
          />
        </div>
      </CardContent>
    </>
  );
}
